import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as groupsApi from '@/services/endpoints/groups'
import * as userApi from '@/services/endpoints/user'
import { useLocalesStore } from '@/stores/locales'
import type { ClaimedPlayer } from '@/types/player'
import type {
  ChangePasswordPayload,
  Group,
  NotificationSettingsPayload,
  Server,
  UpdateProfilePayload,
  User,
} from '@/types/user'

/**
 * Fallback when the account has no timezone: the game server's time, which
 * follows London (UTC+00 in winter, UTC+01 in summer). A fixed offset would be
 * an hour off for half the year.
 */
export const DEFAULT_TIME_ZONE = 'Europe/London'

export type GroupRoleName = 'admin' | 'def' | 'off' | 'member'

/** Checks that a timezone string is one Intl actually understands. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone })
    return true
  } catch {
    return false
  }
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)

  const groups = computed<Group[]>(() => user.value?.groups ?? [])

  /** The backend guarantees at most one group with `role.selected === true`. */
  const selectedGroup = computed<Group | null>(
    () => groups.value.find((group) => group.role.selected) ?? null,
  )

  const favoriteServers = computed<Server[]>(() => user.value?.favorite_servers ?? [])

  const claimedPlayers = computed<ClaimedPlayer[]>(() => user.value?.players ?? [])

  /**
   * The player claimed on a given server, or null. The backend guarantees at
   * most one per server (UserController::attachPlayer detaches the previous
   * one), so the first match is the only match.
   */
  function playerOnServer(serverId: number): ClaimedPlayer | null {
    return claimedPlayers.value.find((player) => player.server_id === serverId) ?? null
  }

  /**
   * True when the user belongs to a group on that server: the character
   * claimed there is then locked (see the backend's `PLAYER_LOCKED_BY_GROUP`),
   * and the profile must not offer to change or release it until they leave
   * the group.
   */
  function isPlayerLockedOnServer(serverId: number): boolean {
    return groups.value.some((group) => group.server_id === serverId)
  }

  const displayName = computed(() => {
    const current = user.value
    if (!current) return ''
    return current.name?.trim() || current.lobby_name?.trim() || current.email.split('@')[0] || ''
  })

  /** Two-letter monogram used when there is no avatar. */
  const initials = computed(() => {
    const source = displayName.value.trim()
    if (!source) return '?'
    const parts = source.split(/\s+/).filter(Boolean)
    const first = parts[0]?.charAt(0) ?? ''
    const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''
    return (first + second).toUpperCase() || '?'
  })

  const avatarUrl = computed(() => user.value?.propic?.trim() || null)

  /** True when the avatar is a file we store, as opposed to a pasted link. */
  const avatarIsUploaded = computed(() => Boolean(user.value?.propic_path))

  /** Valid IANA zone from the profile, or null to mean "use the Europe/London default". */
  const timeZone = computed<string | null>(() => {
    const zone = user.value?.timezone?.trim()
    if (!zone) return null
    return isValidTimeZone(zone) ? zone : null
  })

  const roleInSelectedGroup = computed<GroupRoleName | null>(() => {
    const group = selectedGroup.value
    if (!group) return null
    if (group.role.admin) return 'admin'
    if (group.role.def) return 'def'
    if (group.role.off) return 'off'
    return 'member'
  })

  /** Social-only accounts have no password to confirm when changing it. */
  const hasPassword = computed(() => user.value?.has_password !== false)

  /* ------------------------------ global roles ------------------------------ */

  const isAdmin = computed(() => user.value?.admin === true)
  const isEditor = computed(() => user.value?.editor === true)

  /**
   * May open the admin panel. Editors get in too, but with fewer sections —
   * everything except Menu and Users (see the admin routes).
   */
  const isStaff = computed(() => isAdmin.value || isEditor.value)

  /**
   * Applies the account's language preference without overwriting a guest choice.
   *
   * Da quando le traduzioni arrivano da API l'operazione è asincrona, e resta
   * volutamente non attesa: il profilo è già stato caricato e la pagina può
   * disegnarsi nella lingua precedente mentre i messaggi nuovi arrivano. La
   * validazione della lingua non è più qui — lo store `locales` sa quali esistono
   * e ripiega da sé se questa è stata disattivata dal pannello.
   */
  function applyUserLocale(): void {
    const language = user.value?.language?.trim()
    if (!language) return

    // `persist: false`: la preferenza dell'account vince a runtime ma non
    // sovrascrive la scelta che l'ospite ha salvato in questo browser.
    void useLocalesStore()
      .use(language, { persist: false })
      .catch(() => undefined)
  }

  async function fetchUser(): Promise<User> {
    loading.value = true
    try {
      const fetched = await userApi.getUser()
      user.value = fetched
      applyUserLocale()
      return fetched
    } finally {
      loading.value = false
    }
  }

  function clear(): void {
    user.value = null
  }

  async function updateProfile(payload: UpdateProfilePayload): Promise<User> {
    const updated = await userApi.updateProfile(payload)
    // The PATCH response carries no relations, so keep the ones we already have.
    user.value = user.value ? { ...user.value, ...updated } : updated
    applyUserLocale()
    return user.value
  }

  async function uploadAvatar(file: File, onProgress?: (percent: number) => void): Promise<User> {
    const updated = await userApi.uploadPropic(file, onProgress)
    // Same as updateProfile: the response carries no relations, keep ours.
    user.value = user.value ? { ...user.value, ...updated } : updated
    return user.value
  }

  async function removeAvatar(): Promise<User> {
    const updated = await userApi.deletePropic()
    user.value = user.value ? { ...user.value, ...updated } : updated
    return user.value
  }

  function changePassword(payload: ChangePasswordPayload) {
    return userApi.changePassword(payload)
  }

  async function updateNotificationSettings(payload: NotificationSettingsPayload) {
    const updated = await userApi.updateNotificationSettings(payload)
    user.value = user.value ? { ...user.value, ...updated } : updated
    return user.value
  }

  async function addFavoriteServer(serverId: number): Promise<Server[]> {
    const servers = await userApi.addFavoriteServer(serverId)
    if (user.value) user.value = { ...user.value, favorite_servers: servers }
    return servers
  }

  async function removeFavoriteServer(serverId: number): Promise<Server[]> {
    const servers = await userApi.removeFavoriteServer(serverId)
    if (user.value) {
      // The backend releases that server's player in the same transaction but
      // answers with the servers only, so the local list has to be pruned here
      // — otherwise a released character would linger in the UI until reload.
      user.value = {
        ...user.value,
        favorite_servers: servers,
        players: claimedPlayers.value.filter((player) => player.server_id !== serverId),
      }
    }
    return servers
  }

  /** Same local pruning as `removeFavoriteServer`: see `endpoints/user.ts` for the backend side. */
  async function removeArchivedServer(serverId: number): Promise<Server[]> {
    const servers = await userApi.removeArchivedServer(serverId)
    if (user.value) {
      user.value = {
        ...user.value,
        favorite_servers: servers,
        players: claimedPlayers.value.filter((player) => player.server_id !== serverId),
      }
    }
    return servers
  }

  async function claimPlayer(playerId: number): Promise<ClaimedPlayer[]> {
    const players = await userApi.claimPlayer(playerId)
    if (user.value) user.value = { ...user.value, players }
    return players
  }

  async function releasePlayer(playerId: number, confirm = false): Promise<ClaimedPlayer[]> {
    const players = await userApi.releasePlayer(playerId, confirm)
    if (user.value) user.value = { ...user.value, players }
    return players
  }

  /**
   * Switches the active group. The caller must reload the menu afterwards:
   * the backend recomputes it from the selected group's role.
   */
  async function selectGroup(groupId: number): Promise<void> {
    await groupsApi.selectGroup(groupId)
    await fetchUser()
  }

  async function unselectGroup(): Promise<void> {
    await groupsApi.unselectGroup()
    await fetchUser()
  }

  async function leaveGroup(groupId: number): Promise<void> {
    await groupsApi.leaveGroup(groupId)
    await fetchUser()
  }

  return {
    user,
    loading,
    groups,
    selectedGroup,
    favoriteServers,
    claimedPlayers,
    playerOnServer,
    isPlayerLockedOnServer,
    displayName,
    initials,
    avatarUrl,
    avatarIsUploaded,
    timeZone,
    roleInSelectedGroup,
    hasPassword,
    isAdmin,
    isEditor,
    isStaff,
    fetchUser,
    clear,
    updateProfile,
    uploadAvatar,
    removeAvatar,
    changePassword,
    updateNotificationSettings,
    addFavoriteServer,
    removeFavoriteServer,
    removeArchivedServer,
    claimPlayer,
    releasePlayer,
    selectGroup,
    unselectGroup,
    leaveGroup,
  }
})
